import { useEffect, useState } from 'react'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { Fab, Tooltip, Zoom } from '@mui/material'
import { useInsightsEnabled } from 'hooks/use-insights-enabled'
import { resolveInsightsRoute, useInsightsScope } from 'hooks/use-insights-scope'
import { InsightsDrawer } from 'v2/pages/Insights/InsightsDrawer'

/**
 * A única entrada do Insights.
 *
 * Aparece na tela de contratos de um cliente e em qualquer tela dentro de um
 * contrato, para a corretora e para o RH da empresa cliente, e abre já naquele
 * CNPJ (dentro de um contrato, com ele escolhido; para o RH, o CNPJ é sempre o
 * da própria empresa):
 * escolher o cliente é o trabalho que o usuário acabou de fazer, e repetir a
 * pergunta numa tela vazia seria desfazer isso. Fora de um cliente não há
 * escopo, então não há botão; o menu lateral também não tem item.
 */
const InsightsFab = () => {
  const { enabled, qualifyingBuyerIds } = useInsightsEnabled()
  const scope = useInsightsScope()
  const [open, setOpen] = useState(false)
  // O painel carrega workspaces de todos os fornecedores; montar antes do
  // primeiro clique cobraria isso em toda tela de cliente. Depois de aberto ele
  // fica montado, para o chat e os campos sobreviverem a fechar e reabrir.
  const [openedFor, setOpenedFor] = useState<string | null>(null)

  const organizationId = scope?.organizationId ?? ''
  // Cliente e contrato da tela: trocar qualquer um dos dois abre o painel de novo
  // no lugar certo.
  const scopeKey = `${organizationId}|${scope?.workspaceId ?? ''}`

  // Trocar de cliente descarta o painel: o chat e os campos preenchidos são
  // daquele CNPJ, e reaproveitá-los no próximo seria mostrar a resposta de um
  // cliente na tela de outro. Trocar de contrato também, para abrir nele.
  useEffect(() => {
    setOpen(false)
    setOpenedFor(null)
  }, [scopeKey])

  if (!enabled || !scope) {
    // Botão ausente é indepurável: de fora não dá para distinguir "sem
    // permissão" de "rota errada" de "bug". Em dev, diz qual dos dois foi.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug('[insights-fab] oculto:', {
        motivo: !enabled
          ? 'feature flag desligada'
          : resolveInsightsRoute(window.location.pathname)?.kind === 'contrato'
            ? 'contrato: cliente ainda não resolvido ou falha ao obter'
            : 'rota fora de um cliente',
        pathname: window.location.pathname,
        qualifyingBuyerIds,
      })
    }
    return null
  }

  // Deliberadamente não filtramos pela allowlist de buyers aqui. Esconder o
  // botão por ela transformaria "este cliente não tem dado" em "a função não
  // existe", que é o mesmo erro que o painel evita ao manter o card
  // indisponível na tela com o motivo escrito. O painel diz o que houve.

  const handleOpen = () => {
    setOpenedFor(scopeKey)
    setOpen(true)
  }

  return (
    <>
      {/* Some enquanto o painel está aberto: em tela cheia ele não teria função,
          e um botão de abrir flutuando sobre o que já está aberto sugere uma
          segunda janela. `Zoom` evita o corte seco na volta. */}
      <Zoom in={!open} unmountOnExit>
        <Tooltip title="Insights deste cliente">
          <Fab
            onClick={handleOpen}
            aria-label="Abrir Insights deste cliente"
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              // Abaixo do drawer (1200) de propósito: nenhuma sobreposição do
              // app pode ficar atrás dele.
              zIndex: (theme) => theme.zIndex.drawer - 1,
              width: 56,
              height: 56,
              backgroundColor: '#3024E1',
              color: '#FFFFFF',
              boxShadow: '0 4px 12px rgba(48,36,225,0.3)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              '&:hover': {
                backgroundColor: '#3024E1',
                transform: 'scale(1.05)',
                boxShadow: '0 6px 18px rgba(48,36,225,0.4)',
              },
            }}
          >
            <AutoAwesomeIcon sx={{ color: '#FFFFFF !important' }} />
          </Fab>
        </Tooltip>
      </Zoom>

      {openedFor === scopeKey && (
        <InsightsDrawer
          open={open}
          onClose={() => setOpen(false)}
          scope={scope}
        />
      )}
    </>
  )
}

export default InsightsFab
